/**
 * Display mode (owner decision 2026-09-25): light and dark.
 *
 * The first visit follows the system setting; an explicit choice is stored
 * and wins afterwards. index.html applies the same rule before the first
 * paint (no flash), and ThemeProvider keeps it in sync at runtime.
 */
export type Theme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'aqs.theme';

export function isTheme(value: unknown): value is Theme {
  return value === 'light' || value === 'dark';
}

export function readStoredTheme(): Theme | null {
  try {
    const value = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isTheme(value) ? value : null;
  } catch {
    return null;
  }
}

export function storeTheme(theme: Theme): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Private mode / blocked storage: the choice still applies for this visit.
  }
}

export function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' && (window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false);
}

/** Stored choice first, otherwise the system setting. */
export function resolveTheme(stored: Theme | null, prefersDark: boolean): Theme {
  return stored ?? (prefersDark ? 'dark' : 'light');
}

export function applyTheme(theme: Theme, root: HTMLElement = document.documentElement): void {
  root.dataset.theme = theme;
  root.style.colorScheme = theme;
}
